import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, InputSignal, output, OutputEmitterRef, Signal } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { TableCellDirective, TableColumnDirective, TableComponent, TableHeaderDirective } from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import { EmployeeModel, EmployeeStatus } from '@libs/account/models';

interface EmployeeStatusChipModel {
  className: string;
  label: string;
}

type EmployeeWithStatusModel = EmployeeModel & {
  statusChip: EmployeeStatusChipModel;
};

@Component({
  selector: 'ui-employee-list',
  templateUrl: './employee-list.component.html',
  styleUrls: ['./employee-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TableComponent,
    TableColumnDirective,
    TableHeaderDirective,
    TableCellDirective,
    IconComponent,
    MatIconButton,
    MatTooltip,
    NgClass,
  ],
})
export class EmployeeListComponent {
  public readonly employees: InputSignal<EmployeeModel[]> = input.required<EmployeeModel[]>();
  public readonly renewInvitationClicked: OutputEmitterRef<EmployeeModel> = output<EmployeeModel>();
  public readonly editClicked: OutputEmitterRef<EmployeeModel> = output<EmployeeModel>();
  public readonly deleteClicked: OutputEmitterRef<EmployeeModel> = output<EmployeeModel>();

  public readonly statusChips: Record<EmployeeStatus, EmployeeStatusChipModel> = {
    active: { label: 'aktiv', className: 'status-chip-active' },
    pending: { label: 'eingeladen', className: 'status-chip-pending' },
    accepted: { label: 'bestätigt', className: 'status-chip-expired' },
    expired: { label: 'abgelaufen', className: 'status-chip-expired' },
  };

  public readonly employeesWithStatusChip: Signal<EmployeeWithStatusModel[]> = computed(() => {
    return this.employees().map((employee) => ({
      ...employee,
      statusChip: this.getStatusChip(employee.status),
    }));
  });

  public getStatusChip(status: EmployeeStatus): EmployeeStatusChipModel {
    if (Object.hasOwn(this.statusChips, status)) {
      return this.statusChips[status];
    }

    return {
      label: 'unbekannt',
      className: 'status-chip-expired',
    };
  }

  public onDelete(employee: EmployeeModel): void {
    this.deleteClicked.emit(employee);
  }

  public onRenewInvitation(employee: EmployeeModel): void {
    this.renewInvitationClicked.emit(employee);
  }

  public onEdit(employee: EmployeeModel): void {
    this.editClicked.emit(employee);
  }
}
